import { useEffect, useRef, useState } from 'react';
import {
  Activity, AlertCircle, AlertTriangle, CheckCircle2, Loader2, Signal, ToggleLeft,
} from 'lucide-react';
import espnowService, {
  type DiagnosticResult,
  type EspNowRouter,
  getRelayState,
  relayCountFor,
} from '../../services/espnowIrrigation';
import {
  answeredFromDetail, isSettled, signalQuality, stepVerdict, summariseTest,
  type TestStep,
} from '../../utils/nodeSelfTest';
import { errorMessage } from '../../utils/apiResponse';
import { nodeNoun } from '../../utils/nodeLabel';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';

/**
 * "Does this one work?" — asked of a single node, from its own card.
 *
 * Everything the mesh diagnostics panel can do, it does to the whole site: Read
 * Signal walks every node and answers "4/6 node(s) reported RSSI", which names
 * neither the two that failed nor the one you are standing under. On a site
 * with a dozen nodes that is minutes of waiting to learn something about a node
 * you did not ask about.
 *
 * This queues the same two reads scoped to one address — signal, then output
 * pins — waits for both acks, waits again for the reading itself to come up the
 * bridge's outbox, and says what happened in a sentence. No new bridge command:
 * see utils/nodeSelfTest.ts for why the verdict reads the ack and the node row
 * together rather than either one alone.
 */

interface Props {
  controllerId: string;
  router: EspNowRouter;
  /** The CONTROLLER's transport - diagnostics are an XBee-bridge feature. */
  transport?: string | null;
  /** Commands queue when the site is offline; the button says so rather than hiding. */
  controllerOnline: boolean;
  /** Let the page pull the node list once a test has moved something. */
  onFinished?: () => void;
}

/** How often to ask whether the acks have landed. */
const POLL_MS = 4000;
/**
 * The bridge's own timeout for either read is 120 s and the server calls one
 * stale at 240 s, so this only has to outlast the server's own patience. Past
 * it the panel reports what it has rather than spinning.
 */
const ACK_TIMEOUT_MS = 270_000;
/**
 * Extra wait for the reading after the ack. The bridge drains its outbox at the
 * start of a poll tick and runs commands at the end of one, so a value is
 * always at least one ~15 s tick behind the ack that announced it.
 */
const READING_WAIT_MS = 35_000;

const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

export interface Outcome {
  steps: TestStep[];
  /** The node row as it stood when the test finished. */
  node: EspNowRouter;
  /** True when this run is what moved the signal reading. */
  freshSignal: boolean;
  freshOutputs: boolean;
  tookMs: number;
}

export default function NodeSelfTest({
  controllerId, router, transport, controllerOnline, onFinished,
}: Props) {
  const noun = nodeNoun(transport);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<string | null>(null);
  const [steps, setSteps] = useState<TestStep[] | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The run outlives several awaits; a card can be unmounted by a refresh or a
  // tab change in the middle of one.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  async function run() {
    if (running) return;
    setRunning(true);
    setError(null);
    setOutcome(null);
    setSteps([
      { key: 'signal', label: 'Signal', detail: null, verdict: 'pending' },
      { key: 'outputs', label: 'Outputs', detail: null, verdict: 'pending' },
    ]);
    setPhase('Queued — the bridge picks commands up within about 15 seconds.');

    const mac = router.router_mac;
    // What the row said before we asked. Anything that moves from here is this
    // test's doing, near enough: the alternative reading is ordinary traffic,
    // which is corroboration of the same thing.
    const before = {
      rssi: router.rssi_dbm,
      lastSeen: router.last_seen,
      relayAt: router.relay_state_updated_at,
    };
    const started = Date.now();

    let signalCmd: string;
    let statesCmd: string;
    try {
      // Signal first: it is the one that answers "is it there at all", so when
      // the site only gets through one command before something breaks, it is
      // the more useful one to have got through.
      signalCmd = (await espnowService.runDiagnostic(controllerId, {
        action: 'READ_SIGNAL', router_macs: [mac],
      })).cmd_id;
      statesCmd = (await espnowService.runDiagnostic(controllerId, {
        action: 'READ_STATES', router_macs: [mac],
      })).cmd_id;
    } catch (err) {
      if (!alive.current) return;
      setError(errorMessage(err, `Could not queue the test for this ${noun.oneLower}`));
      setSteps(null);
      setPhase(null);
      setRunning(false);
      return;
    }

    let signalRes: DiagnosticResult | null = null;
    let statesRes: DiagnosticResult | null = null;
    let node: EspNowRouter = router;
    const moved = () => ({
      signal: node.rssi_dbm !== before.rssi || node.last_seen !== before.lastSeen,
      outputs: node.relay_state_updated_at !== before.relayAt,
    });

    const paint = () => {
      const fresh = moved();
      setSteps([
        {
          key: 'signal',
          label: 'Signal',
          detail: signalRes?.detail ?? null,
          verdict: stepVerdict(signalRes, fresh.signal),
        },
        {
          key: 'outputs',
          label: 'Outputs',
          detail: statesRes?.detail ?? null,
          verdict: stepVerdict(statesRes, fresh.outputs),
        },
      ]);
    };

    // Returns rather than assigning, so the assignments happen in the loop body
    // where TypeScript can see them.
    async function fetchState() {
      const [results, routers] = await Promise.all([
        espnowService.getDiagnosticResults(controllerId, 25).catch(() => [] as DiagnosticResult[]),
        espnowService.getRouters(controllerId).catch(() => [] as EspNowRouter[]),
      ]);
      return {
        signal: results.find(r => r.cmd_id === signalCmd) ?? null,
        states: results.find(r => r.cmd_id === statesCmd) ?? null,
        node: routers.find(r => r.router_mac === mac) ?? null,
      };
    }

    // Phase 1 — wait for the site to say what it did.
    const ackDeadline = Date.now() + ACK_TIMEOUT_MS;
    while (Date.now() < ackDeadline && alive.current) {
      await sleep(POLL_MS);
      if (!alive.current) return;
      const next = await fetchState();
      if (!alive.current) return;
      signalRes = next.signal ?? signalRes;
      statesRes = next.states ?? statesRes;
      node = next.node ?? node;
      paint();
      if (signalRes?.status === 'DISPATCHED' || statesRes?.status === 'DISPATCHED') {
        setPhase(`The bridge has the commands and is asking the ${noun.oneLower}.`);
      }
      if (isSettled(signalRes) && isSettled(statesRes)) break;
    }

    // Phase 2 — the ack is not the reading. Wait out a poll tick or two for the
    // value itself, but only when something actually answered: there is nothing
    // to wait for after a silent node, and making somebody watch a spinner for
    // it would be a worse answer than the one we already have.
    const answered = (r: DiagnosticResult | null) => (answeredFromDetail(r?.detail) ?? 0) > 0;
    if (alive.current && (answered(signalRes) || answered(statesRes))) {
      const readingDeadline = Date.now() + READING_WAIT_MS;
      setPhase('Waiting for the reading to reach us — the site uploads on its next cycle.');
      while (Date.now() < readingDeadline && alive.current) {
        const fresh = moved();
        if ((!answered(signalRes) || fresh.signal) && (!answered(statesRes) || fresh.outputs)) break;
        await sleep(POLL_MS);
        if (!alive.current) return;
        const next = await fetchState();
        if (!alive.current) return;
        signalRes = next.signal ?? signalRes;
        statesRes = next.states ?? statesRes;
        node = next.node ?? node;
        paint();
      }
    }

    if (!alive.current) return;
    const fresh = moved();
    setOutcome({
      steps: [
        {
          key: 'signal',
          label: 'Signal',
          detail: signalRes?.detail ?? null,
          verdict: stepVerdict(signalRes, fresh.signal),
        },
        {
          key: 'outputs',
          label: 'Outputs',
          detail: statesRes?.detail ?? null,
          verdict: stepVerdict(statesRes, fresh.outputs),
        },
      ],
      node,
      freshSignal: fresh.signal,
      freshOutputs: fresh.outputs,
      tookMs: Date.now() - started,
    });
    setSteps(null);
    setPhase(null);
    setRunning(false);
    onFinished?.();
  }

  return (
    <div className="border-t border-gray-100 pt-2 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={run}
          disabled={running}
          title={`Ask this ${noun.oneLower} for its signal strength and output state, `
            + 'and report what came back'}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
            border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100
            disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {running
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Activity className="w-3.5 h-3.5" />}
          {running ? 'Testing…' : `Test this ${noun.oneLower}`}
        </button>
        {!running && !outcome && (
          <span className="text-[11px] text-gray-400">
            Signal + outputs, this {noun.oneLower} only. Takes about a minute.
          </span>
        )}
        {!controllerOnline && !running && (
          <span className="text-[11px] text-amber-600">
            Site offline — the test will queue until it reconnects.
          </span>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-1.5 p-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-700">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span>{error}</span>
        </div>
      )}

      {/* In flight. The step rows exist so a long wait still shows which half of
          the test is outstanding, rather than one undifferentiated spinner. */}
      {steps && (
        <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg space-y-1">
          {phase && <p className="text-[11px] text-gray-500">{phase}</p>}
          {steps.map(step => (
            <div key={step.key} className="flex items-center gap-1.5 text-[11px] text-gray-600">
              <StepIcon verdict={step.verdict} />
              <span className="font-medium">{step.label}</span>
              <span className="text-gray-400 truncate">
                {step.detail ?? (step.verdict === 'pending' ? 'waiting…' : '')}
              </span>
            </div>
          ))}
        </div>
      )}

      {outcome && <NodeTestResult outcome={outcome} noun={noun.oneLower} />}
    </div>
  );
}

/**
 * What came back, once. Split out from the run above so the layout can be
 * mounted directly in a viewport test: the interesting widths here belong to
 * strings this panel does not control — a bridge ack runs to 250 characters and
 * lands in a card that is one column wide on a phone.
 */
export function NodeTestResult({ outcome, noun }: { outcome: Outcome; noun: string }) {
  const orgTz = useOrgTimezone();
  const summary = summariseTest(outcome.steps, noun);
  const relayCount = relayCountFor(outcome.node);

  return (
    <div
      className={`p-2 rounded-lg border text-[11px] space-y-1.5 ${
        summary.tone === 'success' ? 'bg-green-50 border-green-200 text-green-800'
          : summary.tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-red-50 border-red-200 text-red-800'
      }`}
    >
      <div className="flex items-start gap-1.5">
        {summary.tone === 'success'
          ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          : summary.tone === 'warn'
            ? <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            : <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />}
        <span className="font-medium">{summary.headline}</span>
      </div>

      {/* The numbers, where there are numbers. The signal line reads off the
          node row rather than the ack, which only ever carries a count. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-700">
        <span className="inline-flex items-center gap-1">
          <Signal className="w-3 h-3 flex-shrink-0" />
          {outcome.node.rssi_dbm === null ? (
            'no signal reading'
          ) : (
            <>
              {outcome.node.rssi_dbm} dBm · {signalQuality(outcome.node.rssi_dbm)}
              {!outcome.freshSignal && (
                <span
                  className="text-gray-400"
                  title="This test did not produce a new reading, so this is the last one on file"
                >
                  {' '}(previous reading)
                </span>
              )}
            </>
          )}
        </span>
        <span className="inline-flex items-center gap-1">
          <ToggleLeft className="w-3 h-3 flex-shrink-0" />
          {outcome.freshOutputs || outcome.node.relay_state_updated_at
            ? `${relayOnCount(outcome.node, relayCount)} of ${relayCount} on`
            : 'outputs never reported'}
          {!outcome.freshOutputs && outcome.node.relay_state_updated_at && (
            <span className="text-gray-400" title="Not re-read by this test">
              {' '}(as of {formatDateTimeInTz(outcome.node.relay_state_updated_at, orgTz)})
            </span>
          )}
        </span>
        <span className="text-gray-400">took {Math.round(outcome.tookMs / 1000)} s</span>
      </div>

      {summary.advice && <p className="text-gray-700">{summary.advice}</p>}

      {/* The bridge's own words. Kept verbatim because on the failure paths they
          are the diagnosis — "the XBee coordinator is disconnected" is not
          something this panel should be paraphrasing. `break-words` is doing
          real work: these acks are one unbroken 250-character sentence and the
          card they sit in is 288px wide on the narrowest phone. */}
      <div className="space-y-0.5 pt-0.5 border-t border-black/10">
        {outcome.steps.map(step => (
          <div key={step.key} className="flex items-start gap-1.5 text-gray-600">
            <StepIcon verdict={step.verdict} />
            <span className="font-medium flex-shrink-0">{step.label}:</span>
            <span className="font-mono break-words min-w-0">
              {step.detail ?? 'no reply recorded'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** How many relays the bitmask says are energised, across the board's real width. */
function relayOnCount(node: EspNowRouter, relayCount: number): number {
  let on = 0;
  for (let i = 0; i < relayCount; i += 1) if (getRelayState(node.relay_states, i)) on += 1;
  return on;
}

function StepIcon({ verdict }: { verdict: TestStep['verdict'] }) {
  if (verdict === 'pending') return <Loader2 className="w-3 h-3 animate-spin text-gray-400 flex-shrink-0 mt-px" />;
  if (verdict === 'answered') return <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-px" />;
  if (verdict === 'silent') return <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-px" />;
  if (verdict === 'refused') return <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-px" />;
  return <AlertTriangle className="w-3 h-3 text-amber-600 flex-shrink-0 mt-px" />;
}
